/* eslint-disable react/function-component-definition */
import React, { useState, useEffect, useContext } from 'react';
import { Row, Col, Table, Badge } from 'react-bootstrap';
import { TableContext } from '../../contexts/tableContext';
import styles from './FieldResolutionForm.module.scss';

const FieldResolutionForm = React.memo(({
  sourceCafe,
  targetCafe,
  value = {},
  onChange,
  readOnly = false
}) => {
  const { formthemeStyle } = useContext(TableContext);
  const [resolutions, setResolutions] = useState({});

  // Initialize with provided value or smart defaults
  useEffect(() => {
    if (Object.keys(value).length > 0) {
      setResolutions(value);
    } else {
      // Set smart defaults - prefer source if it has vendor (claimed), otherwise target
      const defaults = {};
      const preferSource = sourceCafe?.vendor ? 'source' : 'target';
      
      ['name', 'description', 'address', 'location_name', 'images', 'timings',
       'instagram', 'facebook', 'twitter', 'website', 'orderPrefix', 
       'taxPercent', 'preparationTime', 'ccProcessingConfig'].forEach(field => {
        defaults[field] = preferSource;
      });
      
      setResolutions(defaults);
      if (onChange) onChange(defaults);
    }
  }, [sourceCafe, targetCafe]);

  const handleFieldChange = (field, choice) => {
    if (readOnly) return;
    
    const updated = { ...resolutions, [field]: choice };
    setResolutions(updated);
    if (onChange) onChange(updated);
  };

  const formatValue = (value, field) => {
    if (!value && value !== 0) return <span className={styles.naText}>N/A</span>;
    
    switch (field) {
      case 'images':
        if (Array.isArray(value) && value.length > 0) {
          return (
            <div className={styles.imagePreview}>
              {value.slice(0, 3).map((img, idx) => (
                <img key={idx} src={img} alt={`Image ${idx + 1}`} className={styles.thumbnail} />
              ))}
              {value.length > 3 && <Badge bg="secondary">+{value.length - 3} more</Badge>}
              <div className={styles.imageCount}>{value.length} image{value.length !== 1 ? 's' : ''}</div>
            </div>
          );
        }
        return <span className={styles.naText}>No images</span>;
      
      case 'timings':
        if (Array.isArray(value) && value.length > 0) {
          const openDays = value.filter(t => 
            t.periods && t.periods.length > 0 && t.periods.some(p => p.startLocalTime && p.endLocalTime)
          );
          return (
            <div className={styles.timingsPreview}>
              <div className={styles.timingsSummary}>
                {openDays.length} day{openDays.length !== 1 ? 's' : ''} with hours
              </div>
              {openDays.slice(0, 2).map((timing, idx) => (
                <div key={idx} className={styles.timingLine}>
                  <strong>{timing.dayOfWeek}:</strong> {timing.periods[0]?.startLocalTime} - {timing.periods[0]?.endLocalTime}
                  {timing.periods.length > 1 && <Badge bg="info" className="ms-1">+{timing.periods.length - 1}</Badge>}
                </div>
              ))}
              {openDays.length > 2 && <small className={styles.moreText}>+{openDays.length - 2} more days</small>}
            </div>
          );
        }
        return <span className={styles.naText}>No hours set</span>;
      
      case 'ccProcessingConfig':
        if (value && typeof value === 'object') {
          return (
            <div>
              {value.percentage ? `${value.percentage}% ` : ''}
              {value.fixedFee ? `+ $${value.fixedFee} fixed` : ''}
            </div>
          );
        }
        return <span className={styles.naText}>Not configured</span>;
      
      case 'taxPercent':
      case 'preparationTime':
        return <span>{value}{field === 'taxPercent' ? '%' : ' min'}</span>;
      
      default:
        return <span>{String(value)}</span>;
    }
  };

  const getFieldLabel = (field) => {
    const labels = {
      name: 'Cafe Name',
      description: 'Description',
      address: 'Address',
      location_name: 'Location (GPS)',
      images: 'Images',
      timings: 'Operating Hours',
      instagram: 'Instagram',
      facebook: 'Facebook',
      twitter: 'Twitter',
      website: 'Website',
      orderPrefix: 'Order Prefix',
      taxPercent: 'Tax Percentage',
      preparationTime: 'Preparation Time',
      ccProcessingConfig: 'CC Processing Fee'
    };
    return labels[field] || field;
  };

  const fields = [
    { key: 'name', category: 'Basic Info' },
    { key: 'description', category: 'Basic Info' },
    { key: 'address', category: 'Basic Info' },
    { key: 'location_name', category: 'Basic Info' },
    { key: 'images', category: 'Media' },
    { key: 'timings', category: 'Operations' },
    { key: 'instagram', category: 'Social Media' },
    { key: 'facebook', category: 'Social Media' },
    { key: 'twitter', category: 'Social Media' },
    { key: 'website', category: 'Social Media' },
    { key: 'orderPrefix', category: 'POS Settings' },
    { key: 'taxPercent', category: 'POS Settings' },
    { key: 'preparationTime', category: 'POS Settings' },
    { key: 'ccProcessingConfig', category: 'POS Settings' }
  ];

  const groupedFields = fields.reduce((acc, field) => {
    if (!acc[field.category]) acc[field.category] = [];
    acc[field.category].push(field.key);
    return acc;
  }, {});

  const hasValueDifference = (field) => {
    const sourceVal = sourceCafe?.[field];
    const targetVal = targetCafe?.[field];
    
    if (field === 'images') {
      const sourceImages = Array.isArray(sourceVal) ? sourceVal.length : 0;
      const targetImages = Array.isArray(targetVal) ? targetVal.length : 0;
      return sourceImages !== targetImages;
    }
    
    if (field === 'timings') {
      const sourceHours = Array.isArray(sourceVal) ? sourceVal.length : 0;
      const targetHours = Array.isArray(targetVal) ? targetVal.length : 0;
      return sourceHours !== targetHours;
    }
    
    return JSON.stringify(sourceVal) !== JSON.stringify(targetVal);
  };

  return (
    <div className={styles.fieldResolutionContainer}>
      <h5 className={styles.heading}>Select Field Values</h5>
      <p className={styles.subtitle}>
        Choose which cafe's data to keep for each field. Fields with differences are highlighted.
      </p>

      {Object.entries(groupedFields).map(([category, categoryFields]) => (
        <div key={category} className={styles.categorySection}>
          <h6 className={styles.categoryHeading}>{category}</h6>
          <Table bordered hover responsive className={styles.resolutionTable}>
            <thead>
              <tr>
                <th style={{ width: '25%' }}>Field</th>
                <th style={{ width: '37.5%' }}>
                  <Badge bg="primary" className="me-2">Source</Badge>
                  {sourceCafe?.name || 'Source Cafe'}
                </th>
                <th style={{ width: '37.5%' }}>
                  <Badge bg="success" className="me-2">Target</Badge>
                  {targetCafe?.name || 'Target Cafe'}
                </th>
              </tr>
            </thead>
            <tbody>
              {categoryFields.map(field => (
                <tr 
                  key={field}
                  className={hasValueDifference(field) ? styles.differentRow : ''}
                >
                  <td className={styles.fieldLabel}>
                    {getFieldLabel(field)}
                    {hasValueDifference(field) && (
                      <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '0.7rem' }}>
                        Different
                      </Badge>
                    )}
                  </td>
                  <td className={styles.valueCell}>
                    <div className={styles.valueCellContent}>
                      <input
                        type="radio"
                        name={field}
                        checked={resolutions[field] === 'source'}
                        onChange={() => handleFieldChange(field, 'source')}
                        disabled={readOnly}
                        className={styles.radioInput}
                      />
                      <div className={styles.valueContent}>
                        {formatValue(sourceCafe?.[field], field)}
                      </div>
                    </div>
                  </td>
                  <td className={styles.valueCell}>
                    <div className={styles.valueCellContent}>
                      <input
                        type="radio"
                        name={field}
                        checked={resolutions[field] === 'target'}
                        onChange={() => handleFieldChange(field, 'target')}
                        disabled={readOnly}
                        className={styles.radioInput}
                      />
                      <div className={styles.valueContent}>
                        {formatValue(targetCafe?.[field], field)}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ))}

      {!readOnly && (
        <div className={styles.summarySection}>
          <Badge bg="info">
            {Object.values(resolutions).filter(v => v === 'source').length} from Source
          </Badge>
          {' '}
          <Badge bg="success">
            {Object.values(resolutions).filter(v => v === 'target').length} from Target
          </Badge>
        </div>
      )}
    </div>
  );
});

export default FieldResolutionForm;
